import React, { useState, useCallback, useEffect } from 'react';
import { Check, X, Globe, Heart, Crown, Star, Shield, Clock, Sparkles, Lock, Gift, Users, Gem } from 'lucide-react';
import { FaBinoculars } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PaymentService from '../../../services/PaymentService';
import baseURL from '../../authComponents/baseURL/BaseURL';
import PaymentMethodModal from '../../payment/PaymentMethodModal';
import {
  FREE_PLAN_LIMITS,
  ELITE_CONTACT_LINE,
  normalizeFreeFeature,
  isDroppedFreeFeature,
  SIMPLE_FREE_FEATURES,
  SIMPLE_FREE_LIMITS,
} from '../../../utils/freePlanCopy';

const PricingPlans = () => {
  const [billingRegion, setBillingRegion] = useState(null); // null = not yet detected
  const [userSegment, setUserSegment] = useState(null); // null = not yet detected
  // Auto-renewal is not offered on the pricing page: every checkout is one-off.
  const autoRenew = false;
  const [loading, setLoading] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null); // user's active plan key
  const [userId, setUserId] = useState(null);
  const [userName, setUserName] = useState(null); // logged-in user's first name
  const [apiPrices, setApiPrices] = useState(null); // prices fetched from API
  const [pricesLoaded, setPricesLoaded] = useState(false); // /plans/config request finished
  // Bank transfer modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlanForModal, setSelectedPlanForModal] = useState(null); // { key, name, price }
  const [freePlanConfig, setFreePlanConfig] = useState(null);
  const [myDiscount, setMyDiscount] = useState(null); // automatic deen discount for the logged-in member
  const [revealCredit, setRevealCredit] = useState(null); // unused Super Reveal payment, creditable to a plan
  // Admin switch (site setting): when false, men see no Free plan anywhere.
  const [maleFreePlanEnabled, setMaleFreePlanEnabled] = useState(true);
  // Admin switch (site setting): when false, women see no Free plan anywhere.
  const [femaleFreePlanEnabled, setFemaleFreePlanEnabled] = useState(true);
  // False when the signed-in member is a man whose education (Btech/Diploma,
  // Others) disqualifies him from the free plan.
  const [freePlanAvailable, setFreePlanAvailable] = useState(true);
  // Admin switch (site setting): only Free + one Paid plan are on sale.
  const [simplePlans, setSimplePlans] = useState(false);
  const [simplePlanKeys, setSimplePlanKeys] = useState({ male: 'select_paid_male', female: 'select_paid_female' });
  const navigate = useNavigate();

  const isLoggedIn = !!localStorage.getItem('authToken');

  // Auto-detect user gender, marital status, and country from profile
  useEffect(() => {
    const fetchProfile = async () => {
      if (!isLoggedIn) {
        // Not logged in — show female + local as default (they'll be prompted to login on purchase)
        setUserSegment('female');
        setBillingRegion('local');
        setProfileLoaded(true);
        return;
      }
      try {
        const token = localStorage.getItem('authToken');
        const response = await baseURL.get('/profile', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const user = response.data.data;

        // Capture current plan and user name
        setCurrentPlan(user.active_plan || 'free');
        setUserId(user.id || null);
        setUserName(user.first_name || user.full_name || null);

        // Detect gender + marital status → segment.
        // The API returns Rails enum KEYS ('female', 'married'), not the display
        // labels ('Female', 'Married'). Comparing against the labels silently
        // failed, so every logged-in woman was shown the male plans.
        const gender = String(user.gender || '').toLowerCase();
        const maritalStatus = String(user.marital_status || '').toLowerCase();
        if (gender === 'female') {
          setUserSegment('female');
        } else if (maritalStatus === 'married') {
          // Only "Married" (polygamy) users see Discreet/VIP plans.
          // Divorced, widowed, separated users see regular male plans.
          setUserSegment('married');
        } else {
          setUserSegment('male');
        }

        // USD pricing is switched off: everyone (local and overseas) is billed in PKR.
        setBillingRegion('local');
      } catch (err) {
        // Fallback defaults
        setUserSegment('female');
        setBillingRegion('local');
      } finally {
        setProfileLoaded(true);
      }
    };
    fetchProfile();
  }, [isLoggedIn]);

  // Fetch plan prices from the API (admin-editable)
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const res = await baseURL.get('/plans/config');
        if (res.data?.data?.plans) {
          const priceMap = {};
          res.data.data.plans.forEach((p) => {
            // Automatic deen discount (e.g. Rs 1,000 off for practising
            // members): the server sends the member's own price. Show that as
            // the price, with the normal price struck through above it.
            const pkrDeal = p.my_price_pkr != null && p.price_pkr != null && Number(p.my_price_pkr) < Number(p.price_pkr);
            const usdDeal = p.my_price_usd != null && p.price_usd != null && Number(p.my_price_usd) < Number(p.price_usd);
            priceMap[p.key] = {
              price_pkr: pkrDeal ? p.my_price_pkr : p.price_pkr,
              price_usd: usdDeal ? p.my_price_usd : p.price_usd,
              original_price_pkr: pkrDeal ? p.price_pkr : p.original_price_pkr,
              original_price_usd: usdDeal ? p.price_usd : p.original_price_usd,
              deen_applied: pkrDeal || usdDeal,
              label: p.label,
              daily_views: p.daily_views,
              duration_months: p.duration_months,
              bonus_months: p.bonus_months,
              tagline: p.tagline,
              display_features: p.display_features,
            };
          });
          // Also map addons
          if (res.data.data.addons) {
            res.data.data.addons.forEach((a) => {
              priceMap[a.key] = {
                price_pkr: a.price_pkr,
                price_usd: a.price_usd,
                label: a.label,
              };
            });
          }
          setApiPrices(priceMap);
        }
        if (res.data?.data?.super_reveal_credit?.available) {
          setRevealCredit(res.data.data.super_reveal_credit);
        }
        if (res.data?.data?.my_discount?.qualifies) {
          setMyDiscount(res.data.data.my_discount);
        }
        // Load free plan config from API
        const d = res.data?.data;
        if (d) {
          setFreePlanConfig({
            tagline: d.free_plan_tagline || 'Get discovered',
            features: d.free_plan_features || [
              'Browse all profiles with basic filters',
              'Create & manage your profile',
              ELITE_CONTACT_LINE,
              'Upgrade anytime to contact others',
            ],
            daily_views_female: d.new_user_free_views ?? d.free_limits?.female ?? 0,
            daily_views_male: d.new_user_free_views ?? d.free_limits?.male ?? 0,
          });
          if (d.male_free_plan_enabled !== undefined) {
            setMaleFreePlanEnabled(d.male_free_plan_enabled !== false);
          }
          if (d.female_free_plan_enabled !== undefined) {
            setFemaleFreePlanEnabled(d.female_free_plan_enabled !== false);
          }
          if (d.free_plan_available !== undefined) {
            setFreePlanAvailable(d.free_plan_available !== false);
          }
          setSimplePlans(d.simple_plans_enabled === true);
          if (d.simple_plan_keys) setSimplePlanKeys(d.simple_plan_keys);
        }
      } catch (err) {
        // No hardcoded price fallback: showing a stale/wrong amount is worse
        // than showing none. The cards render an unavailable state instead.
        console.warn('Could not fetch plan prices from API');
      } finally {
        setPricesLoaded(true);
      }
    };
    fetchPrices();
  }, []);

  // Helper: get dynamic price for a plan key
  const getPrice = (planKey, field) => {
    if (apiPrices && apiPrices[planKey] && apiPrices[planKey][field] != null) {
      return apiPrices[planKey][field];
    }
    return null;
  };

  // Duration label straight from the plans table. Never guess a number here:
  // quoting "3 months" for a 2-month plan is a promise we can't keep.
  // "6 months" — or "6 months + 6 free" when the plan carries free bonus months.
  const durationLabel = (months, bonus) => {
    if (months == null) return null;
    const base = `${months} ${Number(months) === 1 ? 'month' : 'months'}`;
    const extra = Number(bonus) || 0;
    return extra > 0 ? `${base} + ${extra} bonus` : base;
  };

  // Helper: format a numeric price into display string
  const formatNum = (num) => {
    if (num == null) return null;
    // For PKR: "1,499", for USD: "9.99"
    return num.toLocaleString(undefined, { minimumFractionDigits: num % 1 !== 0 ? 2 : 0 });
  };

  // Handle bank transfer submission from modal
  const handleBankTransferSubmit = useCallback(async (receiptFile, promoCode = null) => {
    if (!selectedPlanForModal) return;
    try {
      const res = await PaymentService.submitBankTransfer(
        selectedPlanForModal.key,
        'pkr',
        receiptFile,
        promoCode
      );
      toast.success(res?.data?.message || 'Payment request submitted!');
      // Clear payment_required flag since receipt was submitted
      localStorage.removeItem("payment_required");
      return res; // Pass response to modal for OCR status
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to submit payment request';
      toast.error(msg);
      throw err; // re-throw so modal knows it failed
    }
  }, [selectedPlanForModal]);

  const handleChoosePlan = useCallback(async (planKey, planDisplayName, planDisplayPrice) => {
    if (!isLoggedIn) {
      toast.info('Please sign up or log in first');
      navigate('/login?action=upgrade');
      return;
    }

    // All plans are paid by bank transfer
    if (planKey !== 'super_reveal_3') {
      setSelectedPlanForModal({
        key: planKey,
        name: planDisplayName || planKey,
        price: planDisplayPrice || '',
      });
      setShowPaymentModal(true);
      return;
    }

    // For add-ons, also show bank transfer modal
    setSelectedPlanForModal({
      key: planKey,
      name: planDisplayName || 'Super Reveal Pack',
      price: planDisplayPrice || '',
    });
    setShowPaymentModal(true);
  }, [isLoggedIn, navigate]);

  /* ─────────────────── PLAN DEFINITIONS ─────────────────── */

  // Every paid plan includes the Profession + Education browse filters. The
  // bullet is added here (instead of in each admin-editable feature list) so
  // the promise on the pricing page can never drift out of sync with what the
  // browse page actually unlocks.
  const PREMIUM_FILTERS_FEATURE = 'Filter by profession & education level';

  const withPremiumFilters = (features) => {
    const already = features.some((f) =>
      String(f.text).toLowerCase().includes('profession') && String(f.text).toLowerCase().includes('education')
    );
    return already ? features : [...features, { text: PREMIUM_FILTERS_FEATURE, included: true }];
  };

  // Helper: build features from API display_features, with hardcoded fallback
  const buildFeatures = (planKey, fallbackFeatures) => {
    const apiFeatures = getPrice(planKey, 'display_features');
    if (apiFeatures && apiFeatures.length > 0) {
      return withPremiumFilters(apiFeatures.map((f) =>
        typeof f === 'string' ? { text: f, included: true } : { text: f.text, included: f.included !== false }
      ));
    }
    return withPremiumFilters(fallbackFeatures);
  };

  // Free card: the admin-configured feature list says what they DO get; the
  // shared limits (src/utils/freePlanCopy.js) say what they don't, in the same
  // words as the signup plan picker.
  const withFreeClarity = (features) => {
    const rest = features
      .filter((f) => !isDroppedFreeFeature(f.text))
      .map((f) => ({ ...f, text: normalizeFreeFeature(f.text) }));
    return [
      ...rest,
      { text: 'Filter by profession & education level', included: false },
      ...FREE_PLAN_LIMITS.map((text) => ({ text, included: false })),
    ];
  };

  const femaleFreePlan = {
      name: 'Free',
      tagline: freePlanConfig?.tagline || 'Start your search',
      key: null,
      features: withFreeClarity((freePlanConfig?.features || [
        'Browse all profiles with basic filters',
        'Create & manage your profile',
        ELITE_CONTACT_LINE,
      ]).map((text) => ({ text, included: true }))),
      localPrice: 'Free',
      overseasPrice: 'Free',
      originalLocalPrice: null,
      originalOverseasPrice: null,
      duration: 'Forever',
      icon: Heart,
      iconBg: 'bg-pink-50',
      iconColor: 'text-pink-400',
      cardBorder: 'border-gray-200',
      buttonStyle: 'bg-gray-900 text-white hover:bg-gray-800',
      popular: false,
    };

  const femalePlans = [
    ...(femaleFreePlanEnabled ? [femaleFreePlan] : []),
    {
      name: getPrice('select_plus_female', 'label') || 'Select Rose',
      tagline: getPrice('select_plus_female', 'tagline') || 'Start connecting',
      key: 'select_plus_female',
      features: buildFeatures('select_plus_female', [
        { text: `${getPrice('select_plus_female', 'daily_views') || 5} contact views per day`, included: true },
        { text: 'Compatibility scores', included: true },
        { text: 'Shortlist profiles', included: true },
        { text: 'WhatsApp support', included: true },
      ]),
      localPrice: formatNum(getPrice('select_plus_female', 'price_pkr')),
      overseasPrice: formatNum(getPrice('select_plus_female', 'price_usd')),
      originalLocalPrice: formatNum(getPrice('select_plus_female', 'original_price_pkr')) || null,
      originalOverseasPrice: formatNum(getPrice('select_plus_female', 'original_price_usd')) || null,
      duration: durationLabel(getPrice('select_plus_female', 'duration_months'), getPrice('select_plus_female', 'bonus_months')),
      icon: Star,
      iconBg: 'bg-rose-50',
      iconColor: 'text-rose-500',
      cardBorder: 'border-rose-200',
      buttonStyle: 'bg-[#a30000] text-white hover:bg-[#8a0000]',
      popular: false,
    },
    {
      name: getPrice('select_pearl', 'label') || 'Select Pearl',
      tagline: getPrice('select_pearl', 'tagline') || 'Most popular for women',
      key: 'select_pearl',
      features: buildFeatures('select_pearl', [
        { text: 'Everything in Rose', included: true },
        { text: `${getPrice('select_pearl', 'daily_views') || 8} contact views per day`, included: true },
        { text: 'See who viewed your profile', included: true },
        { text: 'Compatibility scores', included: true },
      ]),
      localPrice: formatNum(getPrice('select_pearl', 'price_pkr')),
      overseasPrice: formatNum(getPrice('select_pearl', 'price_usd')),
      originalLocalPrice: formatNum(getPrice('select_pearl', 'original_price_pkr')) || null,
      originalOverseasPrice: formatNum(getPrice('select_pearl', 'original_price_usd')) || null,
      duration: durationLabel(getPrice('select_pearl', 'duration_months'), getPrice('select_pearl', 'bonus_months')),
      icon: Gem,
      iconBg: 'bg-fuchsia-50',
      iconColor: 'text-fuchsia-500',
      cardBorder: 'border-fuchsia-200',
      buttonStyle: 'bg-[#a30000] text-white hover:bg-[#8a0000]',
      popular: true,
    },
    {
      name: getPrice('select_queen', 'label') || 'Select Queen',
      tagline: getPrice('select_queen', 'tagline') || 'The royal experience',
      key: 'select_queen',
      features: buildFeatures('select_queen', [
        { text: 'Everything in Pearl', included: true },
        { text: `${getPrice('select_queen', 'daily_views') || 12} contact views per day`, included: true },
        { text: 'See who viewed your profile', included: true },
        { text: 'Sort profiles by compatibility', included: true },
      ]),
      localPrice: formatNum(getPrice('select_queen', 'price_pkr')),
      overseasPrice: formatNum(getPrice('select_queen', 'price_usd')),
      originalLocalPrice: formatNum(getPrice('select_queen', 'original_price_pkr')) || '3,999',
      originalOverseasPrice: formatNum(getPrice('select_queen', 'original_price_usd')) || '29.99',
      duration: durationLabel(getPrice('select_queen', 'duration_months'), getPrice('select_queen', 'bonus_months')),
      icon: Crown,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-500',
      cardBorder: 'border-amber-300',
      buttonStyle: 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600',
      popular: false,
      launchBadge: getPrice('select_queen', 'original_price_pkr') ? '🔥 Launch Price' : null,
    },
  ];

  const maleFreePlan = {
    name: 'Free',
    tagline: freePlanConfig?.tagline || 'Get discovered',
    key: null,
    features: withFreeClarity((freePlanConfig?.features || [
      'Browse all profiles with basic filters',
      ELITE_CONTACT_LINE,
    ]).map((text) => ({ text, included: true }))),
    localPrice: 'Free',
    overseasPrice: 'Free',
    originalLocalPrice: null,
    originalOverseasPrice: null,
    duration: 'Forever',
    icon: Heart,
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-400',
    cardBorder: 'border-gray-200',
    buttonStyle: 'bg-gray-900 text-white hover:bg-gray-800',
    popular: false,
  };

  const malePlans = [
    ...(maleFreePlanEnabled && freePlanAvailable ? [maleFreePlan] : []),
    {
      name: getPrice('select_plus_male', 'label') || 'Select Plus',
      tagline: getPrice('select_plus_male', 'tagline') || 'Early Bird Offer',
      key: 'select_plus_male',
      features: buildFeatures('select_plus_male', [
        { text: `${getPrice('select_plus_male', 'daily_views') || 3} contact views per day`, included: true },
        { text: 'Shortlist profiles', included: true },
        { text: 'WhatsApp support', included: true },
      ]),
      localPrice: formatNum(getPrice('select_plus_male', 'price_pkr')),
      overseasPrice: formatNum(getPrice('select_plus_male', 'price_usd')),
      originalLocalPrice: formatNum(getPrice('select_plus_male', 'original_price_pkr')) || '5,000',
      originalOverseasPrice: formatNum(getPrice('select_plus_male', 'original_price_usd')) || '19.99',
      duration: durationLabel(getPrice('select_plus_male', 'duration_months'), getPrice('select_plus_male', 'bonus_months')),
      icon: Shield,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
      cardBorder: 'border-blue-200',
      buttonStyle: 'bg-[#a30000] text-white hover:bg-[#8a0000]',
      popular: false,
      launchBadge: '🔥 Early Bird',
    },
    {
      name: getPrice('select_gold', 'label') || 'Select Gold',
      tagline: getPrice('select_gold', 'tagline') || 'Most popular choice',
      key: 'select_gold',
      features: buildFeatures('select_gold', [
        { text: 'Everything in Plus', included: true },
        { text: `${getPrice('select_gold', 'daily_views') || 6} contact views per day`, included: true },
        { text: 'Compatibility scores', included: true },
      ]),
      localPrice: formatNum(getPrice('select_gold', 'price_pkr')),
      overseasPrice: formatNum(getPrice('select_gold', 'price_usd')),
      originalLocalPrice: formatNum(getPrice('select_gold', 'original_price_pkr')) || '5,999',
      originalOverseasPrice: formatNum(getPrice('select_gold', 'original_price_usd')) || '39.99',
      duration: durationLabel(getPrice('select_gold', 'duration_months'), getPrice('select_gold', 'bonus_months')),
      icon: Star,
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-600',
      cardBorder: 'border-purple-300',
      buttonStyle: 'bg-[#a30000] text-white hover:bg-[#8a0000]',
      popular: true,
      launchBadge: getPrice('select_gold', 'original_price_pkr') ? '🔥 Launch Price' : null,
    },
    {
      name: getPrice('select_elite', 'label') || 'Select Elite',
      tagline: getPrice('select_elite', 'tagline') || 'For serious families',
      key: 'select_elite',
      features: buildFeatures('select_elite', [
        { text: 'Everything in Gold', included: true },
        { text: `${getPrice('select_elite', 'daily_views') || 10} contact views per day`, included: true },
        { text: 'Sort profiles by compatibility', included: true },
      ]),
      localPrice: formatNum(getPrice('select_elite', 'price_pkr')),
      overseasPrice: formatNum(getPrice('select_elite', 'price_usd')),
      originalLocalPrice: formatNum(getPrice('select_elite', 'original_price_pkr')) || '9,999',
      originalOverseasPrice: formatNum(getPrice('select_elite', 'original_price_usd')) || '59.99',
      duration: durationLabel(getPrice('select_elite', 'duration_months'), getPrice('select_elite', 'bonus_months')),
      icon: Crown,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600',
      cardBorder: 'border-amber-300',
      buttonStyle: 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600',
      popular: false,
      launchBadge: getPrice('select_elite', 'original_price_pkr') ? '🔥 Launch Price' : null,
    },
  ];

  const marriedPlans = [
    {
      name: getPrice('select_discreet', 'label') || 'Select Discreet',
      tagline: getPrice('select_discreet', 'tagline') || 'Privacy first',
      key: 'select_discreet',
      features: buildFeatures('select_discreet', [
        { text: 'Discreet profile mode', included: true },
        { text: `${getPrice('select_discreet', 'daily_views') || 5} contact views per day`, included: true },
        { text: 'Compatibility scores', included: true },
      ]),
      localPrice: formatNum(getPrice('select_discreet', 'price_pkr')),
      overseasPrice: formatNum(getPrice('select_discreet', 'price_usd')),
      originalLocalPrice: formatNum(getPrice('select_discreet', 'original_price_pkr')) || null,
      originalOverseasPrice: formatNum(getPrice('select_discreet', 'original_price_usd')) || null,
      duration: durationLabel(getPrice('select_discreet', 'duration_months'), getPrice('select_discreet', 'bonus_months')),
      icon: Lock,
      iconBg: 'bg-slate-100',
      iconColor: 'text-slate-600',
      cardBorder: 'border-slate-300',
      buttonStyle: 'bg-[#a30000] text-white hover:bg-[#8a0000]',
      popular: false,
    },
    {
      name: getPrice('select_vip', 'label') || 'Select VIP',
      tagline: getPrice('select_vip', 'tagline') || 'Total privacy & access',
      key: 'select_vip',
      features: buildFeatures('select_vip', [
        { text: 'Everything in Discreet', included: true },
        { text: `${getPrice('select_vip', 'daily_views') || 15} contact views per day`, included: true },
        { text: 'Sort profiles by compatibility', included: true },
      ]),
      localPrice: formatNum(getPrice('select_vip', 'price_pkr')),
      overseasPrice: formatNum(getPrice('select_vip', 'price_usd')),
      originalLocalPrice: formatNum(getPrice('select_vip', 'original_price_pkr')) || null,
      originalOverseasPrice: formatNum(getPrice('select_vip', 'original_price_usd')) || null,
      duration: durationLabel(getPrice('select_vip', 'duration_months'), getPrice('select_vip', 'bonus_months')),
      icon: Crown,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600',
      cardBorder: 'border-amber-300',
      buttonStyle: 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600',
      popular: true,
    },
  ];

  // A tier that says "Everything in <lower tier>" should not repeat the lower
  // tier's bullets, and every paid plan carries the free-extension promise.
  // The promise mirrors each plan's own length: a 6-month plan promises 6
  // months of searching and a 6-month free extension.
  const planMonths = (plan) => {
    const match = /(\d+)/.exec(String(plan.duration || ''));
    return match ? Number(match[1]) : 3;
  };
  const extensionPromise = (months) =>
    `No suitable proposal in ${months} months? We extend your plan ${months} more months at no extra cost`;

  const tidyPlans = (plans) => {
    const seen = new Set();
    return plans.map((plan) => {
      const isPaid = Boolean(plan.key);
      const features = (plan.features || []).filter((f) => {
        const text = String(f.text || '').trim().toLowerCase();
        if (!text) return false;
        if (!isPaid) return true;
        if (!f.included) return true;
        if (text.startsWith('everything in')) return true;
        if (seen.has(text)) return false;
        seen.add(text);
        return true;
      });
      if (!isPaid) return { ...plan, features };
      const hasPromise = features.some((f) =>
        String(f.text || '').toLowerCase().includes('extend your plan')
      );
      return {
        ...plan,
        features: hasPromise
          ? features
          : [
              ...features,
              { text: extensionPromise(planMonths(plan)), included: true, highlight: true },
            ],
      };
    });
  };

  // ── Simple plans mode: Free + Paid only, same for men and women ──
  const simpleGender = userSegment === 'female' ? 'female' : 'male';
  // Admin Free switch for the segment on screen (married men count as men).
  const freeForSegment = simpleGender === 'female' ? femaleFreePlanEnabled : maleFreePlanEnabled;
  const simplePaidKey = simplePlanKeys[simpleGender];
  const simplePlanList = [
    {
      name: 'Free',
      tagline: 'Get discovered',
      key: null,
      features: [
        ...SIMPLE_FREE_FEATURES.map((text) => ({ text, included: true })),
        ...SIMPLE_FREE_LIMITS.map((text) => ({ text, included: false })),
      ],
      localPrice: 'Free',
      overseasPrice: 'Free',
      originalLocalPrice: null,
      originalOverseasPrice: null,
      duration: 'Forever',
      icon: Heart,
      iconBg: simpleGender === 'female' ? 'bg-pink-50' : 'bg-blue-50',
      iconColor: simpleGender === 'female' ? 'text-pink-400' : 'text-blue-400',
      cardBorder: 'border-green-200',
      cardBg: 'bg-green-50',
      buttonStyle: 'bg-gray-900 text-white hover:bg-gray-800',
      popular: false,
    },
    {
      name: getPrice(simplePaidKey, 'label') || 'Paid',
      tagline: getPrice(simplePaidKey, 'tagline') || 'Start talking to families',
      key: simplePaidKey,
      features: buildFeatures(simplePaidKey, [
        { text: `${getPrice(simplePaidKey, 'daily_views') || 10} contact views per day`, included: true },
        { text: 'Psychological compatibility scores', included: true },
        { text: 'WhatsApp support', included: true },
      ]),
      localPrice: formatNum(getPrice(simplePaidKey, 'price_pkr')),
      overseasPrice: formatNum(getPrice(simplePaidKey, 'price_usd')),
      originalLocalPrice: formatNum(getPrice(simplePaidKey, 'original_price_pkr')) || null,
      originalOverseasPrice: formatNum(getPrice(simplePaidKey, 'original_price_usd')) || null,
      duration: durationLabel(getPrice(simplePaidKey, 'duration_months'), getPrice(simplePaidKey, 'bonus_months')),
      icon: Crown,
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600',
      cardBorder: 'border-sky-300',
      cardBg: 'bg-sky-50',
      buttonStyle: 'bg-[#a30000] text-white hover:bg-[#8a0000]',
      popular: true,
    },
  ];

  const activePlans = tidyPlans(
    simplePlans
      // The simple Free card has key null, so match on its price label.
      ? simplePlanList.filter((plan) => plan.localPrice !== 'Free' || freeForSegment) :
    userSegment === 'female' ? femalePlans :
    userSegment === 'male' ? malePlans :
    marriedPlans
  );

  const formatPrice = (plan) => {
    if (plan.localPrice === 'Free') return { amount: 'Free', currency: '', period: '', originalAmount: null, originalCurrency: '' };
    const amount = billingRegion === 'local' ? plan.localPrice : plan.overseasPrice;
    if (amount == null) {
      // Price could not be loaded from the API — never invent one.
      return { amount: null, currency: '', period: '', originalAmount: null, originalCurrency: '', unavailable: true };
    }
    if (billingRegion === 'local') {
      return {
        amount: plan.localPrice,
        currency: 'PKR ',
        period: '',
        originalAmount: plan.originalLocalPrice,
        originalCurrency: 'PKR ',
      };
    }
    return {
      amount: plan.overseasPrice,
      currency: '$',
      period: '',
      originalAmount: plan.originalOverseasPrice,
      originalCurrency: '$',
    };
  };

  // Loading state while detecting profile
  if (!profileLoaded || !pricesLoaded) {
    return (
      <div className="bg-gradient-to-b from-gray-50 to-white py-20 px-4">
        <div className="max-w-[1200px] mx-auto text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-48 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-64 mx-auto mb-12"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-96 bg-gray-100 rounded-2xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white py-20 px-4">
      <div className="max-w-[1200px] mx-auto">
        {/* Logged-in indicator */}
        {isLoggedIn && userName && (
          <div className="text-center mb-4">
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 bg-white border border-gray-200 rounded-full px-3 py-1 shadow-sm">
              <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
              Signed in as <span className="font-medium text-gray-700">{userName}</span>
            </span>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-10">
          <span className="block text-[#a30000] text-xs font-bold uppercase tracking-wider mb-2.5">
            PRICING
          </span>
          <h2 className="font-playfair text-3xl md:text-[2.5rem] font-bold text-[#222] mb-4">
            Find Your Perfect Plan
          </h2>
          <p className="text-[#555] max-w-xl mx-auto leading-relaxed">
            {simplePlans
              ? (freeForSegment
                ? 'Two plans, no confusion: browse free, or go Paid to start contacting families.'
                : 'One simple plan: go Paid to start contacting families.')
              : userSegment === 'male'
              ? 'Join at our exclusive early bird rate — price increases as the community grows.'
              : freeForSegment
              ? 'Start free — browse profiles, get discovered, and upgrade when you\'re ready to connect.'
              : 'Browse profiles, then choose a plan when you\'re ready to connect.'}
          </p>
        </div>


        {/* Guarantee + verification — the two reasons to trust us, above the fold */}
        <div className="max-w-3xl mx-auto -mt-4 mb-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-2xl border-2 border-[#a30000] bg-white px-5 py-4 text-center">
            <p className="text-[11px] font-bold uppercase tracking-wider text-[#a30000] mb-1">
              Our guarantee
            </p>
            <p className="text-sm text-[#222] font-semibold leading-snug">
              No suitable proposal in your plan's term? We extend it for the same time again, at no extra cost.
            </p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white px-5 py-4 text-center">
            <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 mb-1">
              Every profile verified
            </p>
            <p className="text-sm text-[#222] font-semibold leading-snug">
              We turn away around 40% of applications.{' '}
              <Link to="/why-we-reject-profiles" className="underline text-emerald-700">
                Why we reject profiles
              </Link>
            </p>
          </div>
        </div>

        {/* Super Reveal credit — their first Rs 1,000 counts towards a plan */}
        {revealCredit?.available && (
          <div className="max-w-2xl mx-auto mb-10">
            <div className="rounded-2xl border border-amber-300 bg-amber-50 px-5 py-4 text-center">
              <p className="font-semibold text-amber-900 text-base">
                Your {revealCredit.currency === 'usd' ? '$' : 'Rs '}{revealCredit.amount.toLocaleString()} {simplePlans ? 'earlier payment' : 'Super Reveal payment'} comes off {simplePlans ? 'the Paid plan' : 'any plan below'}
              </p>
              <p className="text-xs text-amber-800 mt-1">
                Applied automatically at checkout{revealCredit.days_left ? ` — ${revealCredit.days_left} days left` : ''}.
              </p>
            </div>
          </div>
        )}

        {/* Automatic deen discount — only shown here, on the plans screen */}
        {myDiscount?.qualifies && (
          <div className="max-w-2xl mx-auto mb-10">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-center">
              <p className="font-semibold text-emerald-900 text-base mb-1">
                🕌 {myDiscount.headline}
              </p>
              {(myDiscount.reasons || []).map((r, i) => (
                <p key={i} className="text-sm text-emerald-800 leading-relaxed">{r}</p>
              ))}
              {myDiscount.note && (
                <p className="text-xs text-emerald-700 mt-1">{myDiscount.note}</p>
              )}
              <p className="text-xs text-emerald-700 mt-1">
                It is applied automatically at checkout — no code needed.
              </p>
            </div>
          </div>
        )}

        {/* Segment & Region selectors — only shown when NOT logged in */}
        {!isLoggedIn && (
          <>
            {/* Segment Selector */}
            <div className="flex justify-center mb-6">
              <div className="bg-white p-1.5 rounded-2xl shadow-md border border-gray-200 inline-flex flex-wrap justify-center gap-1">
                {[
                  { id: 'female', label: '👩 Female' },
                  { id: 'male', label: '👨 Male' },
                ].map((seg) => (
                  <button
                    key={seg.id}
                    onClick={() => setUserSegment(seg.id)}
                    className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 ${
                      userSegment === seg.id
                        ? 'bg-[#a30000] text-white shadow-sm'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {seg.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Billing toggle removed — all pricing is shown in PKR, local and overseas. */}

          </>
        )}

        {/* Logged-in user info badge */}
        {isLoggedIn && (
          <div className="flex justify-center mb-12">
            <div className="bg-white px-5 py-2.5 rounded-full shadow-sm border border-gray-200 inline-flex items-center gap-2 text-sm text-gray-600">
              <span>
                {userSegment === 'female' ? '👩' : userSegment === 'married' ? '💍' : '👨'}
              </span>
              <span className="font-medium text-gray-800">
                {userSegment === 'female' ? 'Female' : userSegment === 'married' && !simplePlans ? 'Married Male' : 'Male'} Plans
              </span>
            </div>
          </div>
        )}

        {/* ── FIRST PAID STEP — Super Reveal Pack ──
            Deliberately placed ABOVE the plan grid. Rs 1,000 for 3 instant
            contacts is an impulse-level decision in Pakistan; a multi-thousand
            rupee plan is a deliberation-level one. Get the first payment, then
            upsell. Do not demote this below the plans again without data. */}
        {!simplePlans && (
        <div className="mb-12 max-w-3xl mx-auto">
          <div className="relative bg-white rounded-2xl p-7 md:p-8 border-2 border-indigo-500 shadow-lg">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-[11px] font-bold tracking-wide px-4 py-1 rounded-full uppercase">
              Start here
            </span>
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="font-playfair text-2xl font-bold text-[#222] mb-1.5">
                  Super Reveal Pack — 3 contacts, instantly
                </h3>
                <p className="text-[#555] text-sm leading-relaxed mb-4">
                  Not ready for a full plan? Unlock the contact details of <strong>3 profiles you choose</strong>,
                  right now. No subscription, no monthly commitment — and your reveals never expire.
                </p>
                <div className="flex flex-wrap items-center gap-3 justify-center md:justify-start">
                  <span className="text-3xl font-bold text-[#222]">
                    {(() => {
                      const srPrice = formatNum(getPrice('super_reveal_3', billingRegion === 'local' ? 'price_pkr' : 'price_usd'));
                      if (srPrice == null) return <span className="text-sm font-medium text-gray-500">Price unavailable</span>;
                      return billingRegion === 'local' ? `PKR ${srPrice}` : `$${srPrice}`;
                    })()}
                  </span>
                  <span className="text-xs text-indigo-700 bg-indigo-50 px-3 py-1 rounded-full font-semibold">
                    One-time · never expires
                  </span>
                  <button
                    onClick={() => handleChoosePlan('super_reveal_3', 'Super Reveal Pack', billingRegion === 'local' ? (formatNum(getPrice('super_reveal_3', 'price_pkr'))) : (formatNum(getPrice('super_reveal_3', 'price_usd'))))}
                    disabled={loading === 'super_reveal_3' || getPrice('super_reveal_3', billingRegion === 'local' ? 'price_pkr' : 'price_usd') == null}
                    className="px-7 py-3 bg-indigo-600 text-white rounded-xl text-sm font-bold hover:bg-indigo-700 transition-all disabled:opacity-60"
                  >
                    {loading === 'super_reveal_3' ? 'Processing...' : 'Unlock 3 Contacts'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <p className="text-center text-xs text-gray-500 mt-4">
            Want unlimited or daily contact views instead? Choose a plan below.
          </p>
        </div>
        )}

        {/* Pricing Cards */}
        <div className={`grid grid-cols-1 md:grid-cols-2 ${
          activePlans.length >= 4 ? 'lg:grid-cols-4' : activePlans.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'
        } gap-6 max-w-5xl mx-auto`}>
          {activePlans.map((plan, index) => {
            const IconComponent = plan.icon;
            const price = formatPrice(plan);
            const isLoading = loading === plan.key;
            // Determine if this is the user's current plan
            const isCurrentPlan = isLoggedIn && currentPlan && (
              (plan.key === null && (currentPlan === 'free' || !currentPlan)) ||
              (plan.key !== null && plan.key === currentPlan)
            );
            return (
              <div
                key={index}
                className={`relative ${plan.cardBg || 'bg-white'} rounded-2xl border-2 ${isCurrentPlan ? 'border-green-400 ring-2 ring-green-200' : plan.cardBorder} transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                  plan.popular ? 'lg:scale-[1.03] shadow-lg' : 'shadow-sm'
                }`}
              >
                {/* Current Plan Badge */}
                {isCurrentPlan && (
                  <div className="absolute -top-3.5 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-green-600 text-white px-5 py-1.5 rounded-full text-xs font-bold shadow-md whitespace-nowrap flex items-center gap-1">
                      <Check className="w-3 h-3" /> CURRENT PLAN
                    </span>
                  </div>
                )}

                {/* Popular Badge (hidden if current plan badge is shown) */}
                {plan.popular && !isCurrentPlan && (
                  <div className="absolute -top-3.5 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#a30000] text-white px-5 py-1.5 rounded-full text-xs font-bold shadow-md whitespace-nowrap">
                      MOST POPULAR
                    </span>
                  </div>
                )}

                {/* Launch Price Badge */}
                {plan.launchBadge && !plan.popular && !isCurrentPlan && (
                  <div className="absolute -top-3.5 left-1/2 transform -translate-x-1/2">
                    <span className="bg-orange-500 text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-md whitespace-nowrap">
                      {plan.launchBadge}
                    </span>
                  </div>
                )}

                {/* Both badges for popular + launch (hidden if current plan) */}
                {plan.launchBadge && plan.popular && !isCurrentPlan && (
                  <div className="absolute -top-3.5 right-4">
                    <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-[10px] font-bold shadow-md whitespace-nowrap">
                      🔥 LAUNCH
                    </span>
                  </div>
                )}

                <div className="p-7">
                  {/* Icon and Title */}
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${plan.iconBg} mb-4`}>
                    <IconComponent className={`w-6 h-6 ${plan.iconColor}`} />
                  </div>
                  <h3 className="text-xl font-bold text-[#222] mb-1">{plan.name}</h3>
                  <p className="text-sm text-gray-500 mb-5">{plan.tagline}</p>

                  {/* Price */}
                  <div className="mb-6">
                    {price.amount === 'Free' ? (
                      <div className="text-4xl font-bold text-[#222]">Free</div>
                    ) : price.unavailable ? (
                      pricesLoaded ? (
                        <div className="text-sm font-medium text-gray-500 py-2">
                          Price unavailable right now — please refresh.
                        </div>
                      ) : (
                        <div className="h-10 w-32 bg-gray-100 rounded animate-pulse" />
                      )
                    ) : (
                      <div>
                        {/* Strikethrough original price if on launch pricing */}
                        {price.originalAmount && (
                          <div className="flex items-baseline mb-1">
                            <span className="text-sm text-gray-400 line-through">
                              {price.originalCurrency}{price.originalAmount}
                            </span>
                            {getPrice(plan.key, 'deen_applied') ? (
                              <span className="ml-2 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                                YOUR DISCOUNT
                              </span>
                            ) : (
                              <span className="ml-2 text-xs font-semibold text-orange-500 bg-orange-50 px-2 py-0.5 rounded-full">
                                LAUNCH OFFER
                              </span>
                            )}
                          </div>
                        )}
                        <div className="flex items-baseline">
                          <span className="text-sm font-medium text-gray-500 mr-1">{price.currency}</span>
                          <span className="text-4xl font-bold text-[#222]">{price.amount}</span>
                        </div>
                      </div>
                    )}
                    <p className="text-sm text-gray-500 mt-1">
                      {plan.localPrice === 'Free'
                        ? 'No credit card needed'
                        : plan.duration
                          ? `for ${plan.duration}`
                          : ''}
                    </p>
                  </div>

                  {/* CTA Button */}
                  {isCurrentPlan ? (
                    <div className="block w-full text-center py-3 rounded-xl font-semibold text-sm mb-7 bg-green-50 text-green-700 border-2 border-green-300 cursor-default">
                      ✓ Current Plan
                    </div>
                  ) : plan.key ? (
                    <button
                      onClick={() => handleChoosePlan(plan.key, plan.name, billingRegion === 'local' ? plan.localPrice : plan.overseasPrice)}
                      disabled={isLoading || price.unavailable}
                      className={`block w-full text-center py-3 rounded-xl font-semibold text-sm transition-all duration-300 mb-7 ${plan.buttonStyle} disabled:opacity-60`}
                    >
                      {isLoading ? (
                        <span className="animate-pulse">Processing...</span>
                      ) : price.unavailable ? (
                        pricesLoaded ? 'Unavailable' : 'Loading…'
                      ) : (
                        'Choose Plan'
                      )}
                    </button>
                  ) : (
                    <Link
                      to="/signup"
                      className={`block w-full text-center py-3 rounded-xl font-semibold text-sm transition-all duration-300 mb-7 ${plan.buttonStyle}`}
                    >
                      Get Started Free
                    </Link>
                  )}

                  {/* Features */}
                  <div className="space-y-3.5">
                    {plan.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start gap-3">
                        {feature.included ? (
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        ) : (
                          <X className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={3} />
                        )}
                        <span className={`text-sm ${feature.highlight ? 'text-[#a30000] font-semibold' : feature.included ? 'text-gray-700' : 'text-red-800'}`}>
                          {feature.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Early Bird Notice for Males */}
        {userSegment === 'male' && !simplePlans && (
          <div className="mt-6 max-w-3xl mx-auto text-center">
            <p className="text-sm text-gray-500 bg-amber-50 border border-amber-200 rounded-xl px-6 py-3 inline-block">
              ⏳ <span className="font-semibold text-amber-700">Early Bird Pricing</span> — Current prices will revise to <span className="font-bold text-gray-700">Rs 10,000+</span> as our community grows. Lock in your rate today.
            </p>
          </div>
        )}

        {/* Referral Section — hidden in simple mode, where Free means no contact */}
        {!simplePlans && (
        <div className="mt-8 max-w-3xl mx-auto">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-100">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 bg-emerald-600 rounded-2xl flex items-center justify-center">
                  <Gift className="w-7 h-7 text-white" />
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="font-playfair text-xl font-bold text-[#222] mb-1.5">
                  Refer & Earn Free Views
                </h3>
                <p className="text-[#555] text-sm leading-relaxed">
                  Share your referral link with friends. When they sign up and get approved, you
                  earn <strong>3 bonus contact views</strong> — completely free! Check your referral link in your profile settings.
                </p>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* How Contact Views Work */}
        <div className="mt-8 max-w-3xl mx-auto bg-gradient-to-r from-[rgba(59,0,0,0.05)] to-[rgba(163,0,0,0.05)] rounded-2xl p-8">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-shrink-0">
              <div className="w-14 h-14 bg-[#a30000] rounded-2xl flex items-center justify-center">
                <FaBinoculars className="w-7 h-7 text-white" />
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="font-playfair text-xl font-bold text-[#222] mb-2">
                How Contact Views Work
              </h3>
              <p className="text-[#555] text-sm leading-relaxed">
                Browse any profile{freeForSegment ? ' for free' : ''}. When you find someone interesting, upgrade to a paid plan to
                see their phone number and WhatsApp — then reach out directly.
                {' '}Paid members also get to view your contact details and reach out to you.
                {' '}Choose a plan that fits your needs and start connecting today.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Info */}
        <div className="text-center mt-12">
          <p className="text-gray-500 mb-4 text-sm">
            Need help choosing? Our team is available on WhatsApp.
          </p>
          <div className="flex justify-center items-center gap-8 max-sm:gap-4 text-sm text-gray-400">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Verified Profiles
            </div>
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Global Reach
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              24/7 Support
            </div>
          </div>
        </div>
      </div>

      {/* Bank Transfer Payment Modal for PKR users */}
      <PaymentMethodModal
        isOpen={showPaymentModal}
        onClose={() => { setShowPaymentModal(false); setSelectedPlanForModal(null); }}
        onSelectCard={() => {}} // Card payment temporarily disabled
        onSubmitBankTransfer={handleBankTransferSubmit}
        planName={selectedPlanForModal?.name || ''}
        planPrice={selectedPlanForModal?.price || ''}
        planKey={selectedPlanForModal?.key || ''}
        userId={userId}
        loading={!!loading}
      />
    </div>
  );
};

export default PricingPlans;
